import { media } from "../../design/breakpoints";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { GlobalState } from "../../GlobalState";
import Loading from "../utils/loading";
import { literToKiloliter } from "../utils/utils";
import { FaChevronRight } from "react-icons/fa";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";

const SensorBar = styled.div`
  position: relative;
  flex: 0 0 320px;
  max-width: 300px;
  min-width: 300px;
  margin: 0.5rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #237A35;
    box-shadow: 0 2px 8px rgba(35, 122, 53, 0.12);
  }

  ${media.max1024} {
    flex: 1 1 calc(40% - 16px);
    max-width: calc(40% - 16px);
    min-width: 240px;
  }

  ${media.max480} {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0.3rem 0;
  }
`;

const AccentBar = styled.div`
  width: 5px;
  flex-shrink: 0;
  background: ${({ $isFaulty, $isOnline }) =>
    $isFaulty ? "#F59E0B" : $isOnline ? "#22C55E" : "#94A3B8"};
`;

const SensorInner = styled.div`
  flex: 1;
  min-width: 0;
  padding: 9px 11px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.08); }
`;

const TopInfoRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  width: 100%;
  min-width: 0;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const BadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const PlainDuration = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  color: #d81111;
  white-space: nowrap;
  animation: ${pulse} 2s infinite ease-in-out;
  transform-origin: center;
`;

const BarRow = styled.div`
  width: 100%;
`;

const BarWrapper = styled.div`
  width: 100%;
  min-width: 0;
  height: 24px;
  background: #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;

  ${media.max768} {
    height: 20px;
  }
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ percent }) => `${Math.min(Math.max(percent, 0), 100)}%`};
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: Arial, sans-serif;
  font-weight: 700;
  font-size: 0.75rem;
  border-radius: 8px;
  transition: width 0.5s ease, background 0.3s ease;
  min-width: ${({ percent }) => (percent > 0 ? "2.5rem" : "0")};

  ${media.max768} {
    font-size: 0.68rem;
  }
`;

/* Shows "— No data" text inside a grey bar when offline */
const OfflineBarLabel = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  padding-left: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4B5563;
`;

const DisplayValue = styled.div`
  font-size: 0.9rem;
  font-family: Arial, sans-serif;
  font-weight: 900;
  color: ${({ color }) => color || "#111827"};
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.3s ease;

  ${media.max768} {
    font-size: 0.75rem;
  }
`;

const Badge = styled.span`
  font-size: 9.5px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $isFaulty, $isOnline }) =>
    $isFaulty
      ? "rgba(245,158,11,.12)"
      : $isOnline
        ? "rgba(34,197,94,.12)"
        : "#F3F4F6"};
  color: ${({ $isFaulty, $isOnline }) =>
    $isFaulty ? "#B45309" : $isOnline ? "#15803D" : "#090a0e"};

  ${media.max768} {
    font-size: 8px;
    padding: 2px 5px;
  }
`;

/* Warning chip shown when reading exceeds max threshold (e.g. 101%) */
const OverflowWarning = styled.span`
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 6px;
  background: rgba(245, 158, 11, 0.15);
  color: #B45309;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #4B5563;
  font-size: 0.65rem;
  flex-shrink: 0;
  transition: color 0.2s ease;

  ${SensorBar}:hover & {
    color: #237A35;
  }
`;

const LocationName = styled.p`
  margin: 0;
  font-weight: 700;
  font-size: 0.85rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111827;
  flex: 1 1 auto;

  ${media.max768} {
    font-size: 0.75rem;
  }
`;



export default function DashboadSensorBoxV2({ sensor_id, type, onStatusReport }) {
  const state = useContext(GlobalState);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [thresholds, setThresholds] = useState({ min: 20, max: 80 });

    useEffect(() => {
    async function fetchThresholds(token, id) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/data?sensor_id=${id}`,
          { headers: { Authorization: token[0] } }
        );
        const d = res.data;
        if (d?.lower_limit != null) {
          setThresholds({
            min: parseInt(d.lower_limit) || 20,
            max: parseInt(d.upper_limit) || 80,
          });
        }
      } catch (err) {
        console.warn("threshold fetch (non-critical):", err.message);
      }
    }
    fetchThresholds(state.token, sensor_id);
  }, [state.token, sensor_id]);

  useEffect(() => {
    async function fetchData(token, id) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/reading?sensor_id=${id}&type=${type}`,
          { headers: { Authorization: token[0] } }
        );
        setList(res.data);
        setLoading(false);
        // FIX #1: report this sensor's live status to the parent dashboard
        if (onStatusReport) {
          const status = res.data?.sensor_status;
          onStatusReport(id, status === "online");
        }
      } catch (err) {
        console.log(err.message);
      }
    }

    fetchData(state.token, sensor_id);
    const interval = setInterval(() => fetchData(state.token, sensor_id), 8000);
    return () => clearInterval(interval);
  }, [state.token, sensor_id, type]);

  if (loading) return <Loading />;

  // const waterLevel = parseFloat(list?.volume || 0);
  // const isFaulty   = list.raw_data === -1;
  // const isOnline   = list.sensor_status === "online";
  // const isOffline  = !isFaulty && !isOnline;
  // const isOverflow = !isFaulty && !isOffline && waterLevel > 100;


const waterLevel = parseFloat(list?.volume || 0);

const isFaulty = list.raw_data === -1;
const isOnline = list.sensor_status === "online";
const isOffline = !isFaulty && !isOnline;
const isOverflow = !isFaulty && !isOffline && waterLevel > 100;

// Use backend color
const GREEN = "#237A35";
const AMBER = "#F59E0B";
const RED_ALERT = "#EA580C";

const getGaugeColor = (pct, minPct, maxPct) => {
  const min = Number(minPct);
  const max = Number(maxPct);
  const hasMin = minPct !== undefined && minPct !== null && !Number.isNaN(min);
  const hasMax = maxPct !== undefined && maxPct !== null && !Number.isNaN(max);

  if (pct > 100 || (hasMax && pct > max)) return AMBER;
  if (hasMin && pct < min) return RED_ALERT;
  return GREEN;
};

const color = isFaulty
  ? RED_ALERT
  : isOffline
  ? "#9CA3AF"
  : getGaugeColor(waterLevel, thresholds.min, thresholds.max);

  // Convert to kL regardless of unit string — sensors return litres
  // Unit field may be "litre", "liter", "L", "Litre" or undefined
  const rawVal = parseFloat(list?.value || 0);
  const displayValue = isOffline
    ? "—"
    : (list.unit?.toLowerCase().startsWith("litr") || list.unit === "L" || rawVal > 100)
      ? literToKiloliter(rawVal)
      : list.value;

  // FIX #5: increased from 15 → 20 chars to avoid truncating "T18 Borewell" etc.
  const MAX_DISPLAY = 20;
  const locationLabel = list.location_name?.length > MAX_DISPLAY
    ? list.location_name.slice(0, MAX_DISPLAY) + "…"
    : list.location_name;

  return (
    // FIX #6: whole card is now clickable with hover state + chevron
    <SensorBar
      onClick={() => history.push(`/sensor/${sensor_id}/${type}`)}
      /* Issue 5: tooltip shows name + status on hover — especially useful when offline */
      title={
        isOffline
          ? `${list.location_name || "Sensor"} — offline (${formatOfflineDuration(list.date, list.time)})`
          : isFaulty
          ? `${list.location_name || "Sensor"} — faulty`
          : undefined
      }>
      <AccentBar $isOnline={isOnline} $isFaulty={isFaulty} />
      <SensorInner>
        <TopInfoRow>
          <LocationName title={list.location_name}>{locationLabel}</LocationName>

          <RightGroup>
            {/* FIX #8: show value when online/faulty, hide only when offline */}
            {!isOffline && (
              <DisplayValue color={color}>{displayValue}</DisplayValue>
            )}

            <BadgeStack>
              {isFaulty && <Badge $isFaulty>faulty</Badge>}
              {isOffline && <Badge $isOnline={false}>offline</Badge>}
              {!isFaulty && isOnline && <Badge $isOnline>online</Badge>}

              {/* FIX #3: overflow warning chip below the status badge */}
              {isOverflow && <OverflowWarning>⚠ overflow</OverflowWarning>}

              {list.sensor_status !== "online" && (
                <PlainDuration>
                  {formatOfflineDuration(list.date, list.time)}
                </PlainDuration>
              )}
            </BadgeStack>

            {/* FIX #6: tap affordance chevron */}
            <ChevronIcon>
              <FaChevronRight />
            </ChevronIcon>
          </RightGroup>
        </TopInfoRow>

        <BarRow>
          {/* FIX #7: offline shows labelled grey bar instead of invisible 0-width bar */}
          <BarWrapper>
            {isOffline ? (
              <OfflineBarLabel>No recent data</OfflineBarLabel>
            ) : (
              <BarFill percent={waterLevel} color={color}>
                {waterLevel}%
              </BarFill>
            )}
          </BarWrapper>
        </BarRow>
      </SensorInner>
    </SensorBar>
  );
}
